import React, { useState } from 'react';
import { Share2, Copy, Check, Loader } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardSubHeader } from '../CardComponents';
import { useFormContext } from '../../contexts/FormContext';
import { showToast, TOAST_TYPES } from '../common/ToastManager';
import { saveRole } from '../../services/roleStorage';
import ExportJsonRole from './export/ExportJsonRole';
import ExportDocxRole from './export/ExportDocxRole';
import { APP_BASENAME } from '../../App';
import { useTranslation } from 'react-i18next';

// Define production URL
const PRODUCTION_URL = 'https://predicthire.com';

const OutputsExport = () => {
  const { t } = useTranslation();
  const { formData } = useFormContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    setIsSharing(true);
    try {
      console.log('[OutputsExport] Saving role with formData:', formData);
      const response = await saveRole(formData);
      console.log('[OutputsExport] Full response from saveRole:', response);
      
      const shareUrl = response?.url;
      console.log('[OutputsExport] Share URL:', shareUrl);
      
      if (!shareUrl) {
        console.error('[OutputsExport] No URL in response:', response);
        throw new Error('Failed to generate share URL');
      }
      
      setShareUrl(shareUrl);
      setIsExpanded(true);
      
      try {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        showToast(t('export.toasts.success'), TOAST_TYPES.SUCCESS);
      } catch (copyError) {
        showToast(t('export.toasts.successManualCopy'), TOAST_TYPES.SUCCESS);
      }
    } catch (error) {
      console.error('[OutputsExport] Error sharing role:', error);
      const errorMessage = error.message === 'Company name is required' || 
                         error.message === 'Job title is required' 
                         ? error.message 
                         : t('export.toasts.error');
      showToast(errorMessage, TOAST_TYPES.ERROR);
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      showToast(t('export.toasts.copied'), TOAST_TYPES.SUCCESS);
    } catch (error) {
      showToast(t('export.toasts.copyError'), TOAST_TYPES.ERROR);
    }
  };

  return (
    <Card className="max-w-5xl mx-auto mt-4">
      <CardHeader 
        data={formData}
        requiredFields={['companyData.name', 'roleData.jobTitle']}
        isExpanded={isExpanded}
        onClick={() => setIsExpanded(!isExpanded)}
        className="bg-purple-50 cursor-pointer"
        alwaysShowInput={true}
        inputSection={
          <button
            onClick={handleShare}
            disabled={isSharing}
            className="px-4 py-2 bg-[#7A1E8B] text-white rounded-md font-medium 
              hover:bg-[#671778] transition-colors duration-200 flex items-center gap-2"
          >
            {isSharing ? (
              <>
                <img 
                  src={`${APP_BASENAME}/loader.gif`} 
                  alt={t('common.loading')} 
                  className="h-4 w-4" 
                />
                <span className="inline-flex items-center">
                  {t('export.buttons.sharing')}
                  <span className="ml-1 animate-pulse">...</span>
                </span>
              </>
            ) : (
              <>
                <Share2 className="h-4 w-4" />
                {t('export.buttons.share')}
              </>
            )}
          </button>
        }
      >
        <div className="flex items-center gap-2">
          <Share2 className="h-5 w-5 text-[#7A1E8B]" />
          <div>
            <CardTitle className="text-gray-900">{t('export.title')}</CardTitle>
            <CardSubHeader className="text-gray-600">
              {t('export.subtitle')}
            </CardSubHeader>
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="p-6">
          <div className="space-y-8">
            {shareUrl && (
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                <p className="text-sm text-purple-700 mb-2">
                  {t('export.shareSuccess')}
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 p-2 bg-white border rounded text-sm"
                  />
                  <button
                    onClick={handleCopy}
                    className="px-3 py-2 bg-purple-100 hover:bg-purple-200 rounded text-purple-700 text-sm font-medium flex items-center gap-2"
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4" />
                        {t('export.buttons.copied')}
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" />
                        {t('export.buttons.copy')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Export options */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-4">{t('export.exportOptions')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <ExportJsonRole />
                <ExportDocxRole />
              </div>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default OutputsExport; 